import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { OBJECT_TYPE_REPORT } from '@app/core/enums/approval-requests.enum';
import { PRIORITY_OBJECT_COLOR, REPORT_STATUS_OBJECT, SORT_BY_DATE } from '@app/shared/const';
import { environment } from '@env/environment';
import { Observable, catchError, delay, map, throwError, timeout } from 'rxjs';
import { ApiClientConfig } from '../api-client.config';
import {
  GetFanpageParams,
  GetGroupParams,
  GetUserFanpageResponse,
  GetUserGroupResponse
} from '../fanpage/api.fanpage.models';
import {
  ApprovalRequestsBody,
  ApprovalRequestsOTABody,
  CensorManagement,
  ReviewListBody
} from '../post/api.post.model';
import { ApiResult } from '../shared/api.result';
import {
  ModerationOverviewDataResponse,
  OverviewData,
  PostListResponse,
  ReportOverviewResponse,
  ReportSystemRequest,
  ReportSystemResponse,
  ReporterItem,
  ReporterSystemRequest,
  VersionSnapshot
} from './api.report.models';

export class ReportApi {
  private reportPath: string = environment.API_URL + '/report/report/';
  private apiUrl = environment.API_URL;
  private POST = '/posts/posts';
  private REPORT = '/report/reporter';
  private MODERATE = '/social-moderator';
  private VERIFICATION = '/verification/admin/verification/history/';
  private PAGE = '/page/pages';
  private GROUP = '/group/groups';

  constructor(
    public readonly http: HttpClient,
    public config: ApiClientConfig
  ) {}

  getReportSystem({
    pageNum,
    pageSize,
    sortByDate
  }: ReportSystemRequest): Observable<ReportSystemResponse> {
    return this.http
      .get<any>(
        this.reportPath + `system?pageNum=${pageNum}&pageSize=${pageSize}&sortByDate=${sortByDate}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map((res: any) => {
          if (res) {
            return res.data;
          }
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getFanpagePostList(
    fanpageId: string,
    status: string[],
    pageSize = 10,
    pageNum = 0,
    sortByDate = SORT_BY_DATE.DESCENDING
  ): Observable<PostListResponse> {
    const params = `pageId=${fanpageId}&status=${status}&pageSize=${pageSize}&pageNum=${pageNum}&sortByDate=${sortByDate}`;
    return this.http.get<PostListResponse>(`${this.apiUrl}${this.POST}/page?${params}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError(err => {
        throw err.error;
      })
    );
  }

  getGroupViewPost(groupId: string, params: any) {
    const apiViewPostGroup = `${this.apiUrl}/posts/posts/group?pageNum=${params.pageNum}&pageSize=${params.pageSize}&groupId=${groupId}&status=${params.status}&sortByDate=${params.sortByDate}`;
    return this.http.get<any[]>(apiViewPostGroup).pipe(
      map((res: any) => {
        return res.data;
      }),
      catchError(err => {
        throw err.error;
      })
    );
  }

  getReportCategoriesAll(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/report/report-category/admin/get-all`).pipe(
      map((res: any) => {
        if (res.success) {
          return res.data;
        }
      }),
      catchError(err => {
        throw err.error;
      })
    );
  }

  getReporterList({
    object_id,
    pageNum = 0,
    pageSize = 10,
    sortByDate = SORT_BY_DATE.DESCENDING
  }: ReporterSystemRequest): Observable<ApiResult.ApiResponse<ReporterItem[]>> {
    const params = new HttpParams()
      .set('pageNum', pageNum)
      .set('pageSize', pageSize)
      .set('sortByDate', sortByDate)
      .set('report_history_id', object_id);
    return this.http
      .get<ApiResult.ApiResponse<ReporterItem[]>>(`${this.apiUrl}${this.REPORT}`, {
        params
      })
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getReportItemOverview(object_id: string): Observable<OverviewData> {
    const path = `${this.apiUrl}/report/report/overview/${object_id}`;
    return this.http.get<ReportOverviewResponse>(path).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        if (res) {
          return res.data;
        }
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getToken(): string | null {
    const auth = localStorage.getItem(environment.TOKEN);
    const authValue = auth ?? '';
    if (authValue !== '') {
      try {
        return JSON.parse(authValue).accessToken; // Parse the JSON string to an object
      } catch (error) {
        console.error('Error parsing authentication token:', error);
        return null;
      }
    } else {
      return null;
    }
  }

  getModule(data: any, isOTA = false): Observable<any> {
    const token = this.getToken();
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);
    const url = `${this.apiUrl}/user/admin/modules/item${isOTA ? '?is_get_ota=true' : ''}`;

    return this.http
      .post<any>(url, data, {
        headers
      })
      .pipe(
        delay(isOTA ? 300 : 0),
        timeout(this.config.responseTimeout),
        map((res: any) => res.data),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getApprovalRequestsListByModule<T>(
    {
      objectType,
      pageNum = 0,
      pageSize = 10,
      sourceTypeArr = 'NEW|EDIT|REPORT',
      priorityTypeArr = 'YELLOW|RED|GREEN',
      process = '',
      keySearch = undefined,
      fromDate = '',
      toDate = '',
      fromUpdateDate = '',
      toUpdateDate = '',
      zip_code = [],
      isLoadAll = false,
      sortByDate = '',
      groupId = ''
    }: ApprovalRequestsBody,
    isOta?: boolean
  ): Observable<ApiResult.ApiResponse<T>> {
    const dateType = objectType === OBJECT_TYPE_REPORT.PRODUCT ? 'created_at' : 'updated_at';
    let params = new HttpParams()
      .set('pageNum', pageNum)
      .set('pageSize', isLoadAll ? pageSize : 1)
      .set('objectType', this.formatObjectType(objectType))
      .set('sourceTypeArr', sourceTypeArr)
      .set('priorityTypeArr', priorityTypeArr)
      .set('fromDate', fromDate)
      .set('toDate', toDate)
      .set('fromUpdateDate', fromUpdateDate)
      .set('toUpdateDate', toUpdateDate)
      .set('root', JSON.stringify(zip_code))
      .set('dateType', dateType)
      .set('isLoadAll', isLoadAll)
      .set('sort', sortByDate)
      .set('groupId', groupId);

    if (keySearch?.length) {
      params = params.set('keySearch', keySearch);
    }

    if (isLoadAll) {
      const convertProcess = process && process.length ? process : 'PENDING|SKIPPED';
      params = params.set('process', convertProcess);
    } else {
      params = params.set('process', 'PENDING');
    }

    const headers = new HttpHeaders().set('Accept', 'application/json');
    const modulePath = isOta ? '/moderate-group/search/content' : '/moderate-contents/search';
    const apiLink = `${this.apiUrl}${this.MODERATE}${modulePath}`;
    const httpOptions = { headers, params };
    return this.http.get<ApiResult.ApiResponse<T>>(apiLink, httpOptions).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        // TODO: Temporary handle view loop with post object data on production
        if (res?.success && res?.data && objectType === 'REEL' && environment.production) {
          return {
            ...res,
            data: res.data.map((item: any) => ({
              ...item,
              object_data: item.object_data ?? item.object_post_data
            }))
          };
        }
        return res;
      }),
      catchError(err => {
        throw err.error;
      })
    );
  }

  getApprovalRequestsOTAListByModule<T>({
    groupType,
    pageNum = 0,
    pageSize = 10,
    sourceTypeArr = 'NEW|EDIT|REPORT',
    priorityTypeArr = 'YELLOW|RED|GREEN',
    keySearch = undefined,
    fromDate = '',
    toDate = '',
    fromUpdateDate = '',
    toUpdateDate = '',
    zip_code = []
  }: ApprovalRequestsOTABody): Observable<ApiResult.ApiResponse<T>> {
    let params = new HttpParams()
      .set('pageNum', pageNum)
      .set('pageSize', pageSize)
      .set('groupType', this.formatObjectType(groupType))
      .set('sourceTypeArr', sourceTypeArr)
      .set('priorityTypeArr', priorityTypeArr)
      .set('fromDate', fromDate)
      .set('toDate', toDate)
      .set('fromUpdateDate', fromUpdateDate)
      .set('toUpdateDate', toUpdateDate)
      .set('root', JSON.stringify(zip_code));
    if (keySearch?.length) {
      params = params.set('keySearch', keySearch);
    }

    const headers = new HttpHeaders().set('Accept', 'application/json');
    const apiLink = `${this.apiUrl}${this.MODERATE}/moderate-group/search`;
    const httpOptions = { headers, params };
    return this.http.get<ApiResult.ApiResponse<T>>(apiLink, httpOptions).pipe(
      timeout(this.config.responseTimeout),
      catchError(err => {
        throw err.error;
      })
    );
  }

  fetchVersionSnapshot(
    id: string,
    type?: string
  ): Observable<ApiResult.ApiResponse<VersionSnapshot>> {
    let params = new HttpParams();
    if (type) {
      params = params.set('object_type', type);
    }

    return this.http
      .get<ApiResult.ApiResponse<VersionSnapshot>>(
        `${environment.BASE_URL}/capture/capture/${id}`,
        { params }
      )
      .pipe(
        timeout(this.config.responseTimeout),
        catchError(err => {
          throw err.error;
        })
      );
  }

  getApprovalRequestsAppealListByModule<T>({
    objectType,
    pageNum = 0,
    pageSize = 10,
    sourceTypeArr = 'NEW|EDIT|REPORT',
    priorityTypeArr = 'YELLOW|RED|GREEN',
    process = '',
    keySearch = undefined,
    fromDate = '',
    toDate = '',
    fromUpdateDate = '',
    toUpdateDate = '',
    zip_code = [],
    isLoadAll = false,
    sortByDate = '',
    groupId = ''
  }: ApprovalRequestsBody): Observable<ApiResult.ApiResponse<T>> {
    const dateType = objectType === OBJECT_TYPE_REPORT.PRODUCT ? 'created_at' : 'updated_at';
    let params = new HttpParams()
      .set('pageNum', pageNum)
      .set('pageSize', isLoadAll ? pageSize : 1)
      .set('objectType', this.formatObjectType(objectType))
      .set('sourceTypeArr', sourceTypeArr)
      .set('priorityTypeArr', priorityTypeArr)
      .set('fromDate', fromDate)
      .set('toDate', toDate)
      .set('fromUpdateDate', fromUpdateDate)
      .set('toUpdateDate', toUpdateDate)
      .set('root', JSON.stringify(zip_code))
      .set('dateType', dateType)
      .set('isLoadAll', isLoadAll)
      .set('sort', sortByDate)
      .set('groupId', groupId);

    if (keySearch?.length) {
      params = params.set('keySearch', keySearch);
    }

    if (isLoadAll) {
      const convertProcess = process && process.length ? process : 'PENDING|SKIPPED';
      params = params.set('process', convertProcess);
    } else {
      params = params.set('process', 'PENDING');
    }

    const headers = new HttpHeaders().set('Accept', 'application/json');
    const modulePath = '/appeal/search';
    const apiLink = `${this.apiUrl}${this.MODERATE}${modulePath}`;
    const httpOptions = { headers, params };
    return this.http.get<ApiResult.ApiResponse<T>>(apiLink, httpOptions).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        // TODO: Temporary handle view loop with post object data on production
        if (res?.success && res?.data && objectType === 'REEL' && environment.production) {
          return {
            ...res,
            data: res.data.map((item: any) => ({
              ...item,
              object_data: item.object_data ?? item.object_post_data
            }))
          };
        }
        return res;
      }),
      catchError(err => {
        throw err.error;
      })
    );
  }

  getReviewListByModule({
    objectType,
    pageNum = 0,
    pageSize = 10,
    actions = [
      REPORT_STATUS_OBJECT.KEPT,
      REPORT_STATUS_OBJECT.WARNED,
      REPORT_STATUS_OBJECT.DELETED
    ],
    priorities = [
      PRIORITY_OBJECT_COLOR.GREEN,
      PRIORITY_OBJECT_COLOR.YELLOW,
      PRIORITY_OBJECT_COLOR.RED
    ],
    fromDate = '',
    toDate = '',
    censoredStartDate = '',
    censoredEndDate = '',
    keySearch = '',
    zip_code = [],
    censors_id = [],
    isAppealList = false
  }: ReviewListBody): Observable<any> {
    const actionStr = `${actions.length ? `&actions=${actions.join('&actions=')}` : ''}`;
    const priorityStr = `${
      priorities.length ? `&priorities=${priorities.join('&priorities=')}` : ''
    }`;
    const approvers = `${
      censors_id.length ? `&censors_id=${censors_id.join('&censors_id=')}` : ''
    }`;
    const type = this.formatObjectType(objectType);
    let params = `objectType=${type}${actionStr}${priorityStr}${approvers}&fromDate=${fromDate}&toDate=${toDate}&censoredStartDate=${censoredStartDate}&censoredEndDate=${censoredEndDate}&pageNum=${pageNum}&pageSize=${pageSize}&sortByDate=DESCENDING`;
    if (keySearch?.length) {
      params = params + `&keySearch=${keySearch}`;
    }
    const rootParam = new HttpParams().set('root', JSON.stringify(zip_code));

    const apiLink = isAppealList
      ? `${this.apiUrl}${this.MODERATE}/appeal/history/search?${params}`
      : `${this.apiUrl}${this.MODERATE}/moderate-contents-history/search?${params}`;
    return this.http.get<any>(apiLink, { params: rootParam }).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        // TODO: Temporary handle view loop with post object data on production
        if (objectType === 'REEL' && environment.production) {
          return res.data.map((item: any) => ({
            ...item,
            object_data: item.object_data ?? item.object_post_data
          }));
        }
        return res.data;
      }),
      catchError(err => {
        throw err.error;
      })
    );
  }

  formatObjectType = (type: string) => {
    const formatted: any = {
      [OBJECT_TYPE_REPORT.ADVERTISEMENT]: OBJECT_TYPE_REPORT.ADS,
      [OBJECT_TYPE_REPORT.PROFILE]: OBJECT_TYPE_REPORT.USERPROFILE
    };
    return formatted[type] || type;
  };

  getInfoDetailsByModule(moduleName: string, id: string) {
    const apiLink = `${this.apiUrl}${this.MODERATE}/moderate-contents/${moduleName}/${id}/owner`;
    return this.http.get<any>(apiLink).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res.data;
      }),
      catchError(err => {
        throw err.error;
      })
    );
  }

  getApprovalHistory(
    { object_id, module, pageNum = 0, pageSize = 10, sortByDate = SORT_BY_DATE.DESCENDING }: any,
    isOta?: boolean
  ): Observable<any> {
    const params = new HttpParams()
      .set('objectType', this.formatObjectType(module))
      .set('pageNum', pageNum)
      .set('pageSize', pageSize)
      .set('sortByDate', sortByDate);

    const modulePath = isOta ? '/moderate-group' : '/moderate-contents-history';
    const url = `${this.apiUrl}${this.MODERATE}${modulePath}/approval-history/${object_id}`;

    return this.http.get<any>(url, { params }).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        if (res) {
          return res.data;
        }
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getApprovalAppealHistory({
    object_id,
    module,
    pageNum = 0,
    pageSize = 10,
    sortByDate = SORT_BY_DATE.DESCENDING
  }: any): Observable<any> {
    const params = new HttpParams()
      .set('objectType', this.formatObjectType(module))
      .set('pageNum', pageNum)
      .set('pageSize', pageSize)
      .set('sortByDate', sortByDate);

    const modulePath = '/appeal/appeal-history/';
    const url = `${this.apiUrl}${this.MODERATE}${modulePath}${object_id}`;

    return this.http.get<any>(url, { params }).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        if (res) {
          return res.data;
        }
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getVerificationHistory({
    object_id,
    objectType,
    pageNum = 0,
    pageSize = 10,
    sortByDate = SORT_BY_DATE.DESCENDING
  }: any): Observable<any> {
    const path = `${this.apiUrl}${this.VERIFICATION}${object_id}?objectType=${objectType}&pageNum=${pageNum}&pageSize=${pageSize}&sortByDate=${sortByDate}`;
    return this.http.get<any>(path).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        if (res) {
          return res.data;
        }
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getEntityModerationOverview(
    objectType: string,
    objectId: string
  ): Observable<ModerationOverviewDataResponse> {
    return this.http
      .get<ModerationOverviewDataResponse>(
        `${this.apiUrl}${this.MODERATE}/moderate-contents/overview/${objectType}/${objectId}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        catchError(err => {
          throw err.error;
        })
      );
  }

  getUserFanpage(params: GetFanpageParams): Observable<GetUserFanpageResponse> {
    const { pageNum, pageSize, textSearch, userId, token } = params;
    const headers = new HttpHeaders()
      .set('Authorization', 'Bearer ' + token)
      .set('owner-user-id', userId);

    return this.http
      .get<GetUserFanpageResponse>(`${this.apiUrl}${this.PAGE}/user-created-pages`, {
        headers,
        params: { pageNum, pageSize, textSearch }
      })
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getUserGroup(params: GetGroupParams): Observable<GetUserGroupResponse> {
    const { pageNum, size, searchText, userId, token } = params;
    const headers = new HttpHeaders()
      .set('Authorization', 'Bearer ' + token)
      .set('owner-user-id', userId);

    return this.http
      .get<GetUserGroupResponse>(`${this.apiUrl}${this.GROUP}/me/my-groups`, {
        headers,
        params: { pageNum, size, searchText }
      })
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getCensorManagement(params: CensorManagement): Observable<any> {
    const queryParams = Object.entries({
      pageNum: params.pageNum,
      pageSize: params.pageSize,
      nodeId: params.nodeId,
      countryCode: params.countryCode,
      type: params.module,
      typeModerator: params.moduleName,
      only_assigned_module: params.only_assigned_module ?? true,
      textSearch: params.textSearch
    })
      .reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc.append(key, value.toString());
        }
        return acc;
      }, new URLSearchParams())
      .toString();

    return this.http.get<any>(`${this.apiUrl}/user/admin/censor-management?${queryParams}`).pipe(
      timeout(this.config.responseTimeout),
      map(response => response?.data?.data),
      catchError(error => throwError(() => error.error))
    );
  }

  getModeratorManagement(params: CensorManagement): Observable<any> {
    const queryParams = Object.entries({
      pageNum: params.pageNum,
      pageSize: params.pageSize,
      nodeId: params.nodeId,
      countryCode: params.countryCode,
      type: params.module,
      typeModerator: params.moduleName,
      only_assigned_module: params.only_assigned_module ?? true,
      textSearch: params.textSearch
    })
      .reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc.append(key, value.toString());
        }
        return acc;
      }, new URLSearchParams())
      .toString();

    return this.http.get<any>(`${this.apiUrl}/user/admin/moderator-management?${queryParams}`).pipe(
      timeout(this.config.responseTimeout),
      map(response => response?.data?.data),
      catchError(error => throwError(() => error.error))
    );
  }
}
